import * as postMessage from 'privates/postMessage';
import get from 'lodash/get';
import {validate} from 'privates/validations/validation';
import platformUtils from 'platformUtils';
import isObject from 'lodash/isObject';
import isUndefined from 'lodash/isUndefined';
import {userErrors} from 'privates/validations/errors';

/**
 * @namespace wix-users
 * @summary The wix-users module contains functionality for working with your
 *  site's users.
 * @description
 *  There are three types of users:
 *
 *  + Visitor - A user who is not logged into your site.
 *  + Member - A user who is logged into your site.
 *  + Admin - The owner of the site.
 *
 *
 *  To use the Users API, import `wixUsers` from the `wix-users` module:
 *
 *    ``` javascript
 *    import wixUsers from 'wix-users';
 *    ```
 *
 * The APIs in `wix-users` can only be used in front-end code.
 *
 * @note
 *  The APIs in `wix-users` are only partially functional when previewing your site.
 *  View a published version of your site to see their complete functionality.
 *
 *  The APIs in `wix-users` can only be used once the page has loaded. Therefore,
 *  you must use them in code that is contained in or is called from the
 *  [onReady()]($w.html#onReady) event handler or any element event handler.
 */

/**
 * @class wix-users.User
 * @summary A site user.
 */

/**
 * @member id
 * @label property
 * @syntax get id(): String
 * @summary Gets the user's ID.
 * @type {external:String}
 * @memberof wix-users.User
 * @description
 * The unique ID of the user.
 * @note
 *  The APIs in `wix-users` are only partially functional when previewing your site.
 *  View a published version of your site to see their complete functionality.
 *
 *  The APIs in `wix-users` can only be used once the page has loaded. Therefore,
 *  you must use them in code that is contained in or is called from the
 *  [onReady()]($w.html#onReady) event handler or any element event handler.
 * @snippet [User-id.es6=Get the user's ID]
 * @snippet [Users-currentUser.es6=Get the current user's information]
 * @readonly
 */

/**
 * @member loggedIn
 * @label property
 * @syntax get loggedIn(): Boolean
 * @summary Indicates whether the user is logged in or not.
 * @type {external:Boolean}
 * @memberof wix-users.User
 * @description
 *  Gets one of the following:
 *  + `true` — The user is logged in.
 *  + `false` — The user is not logged in.
 *
 *
 * @note
 *  The APIs in `wix-users` are only partially functional when previewing your site.
 *  View a published version of your site to see their complete functionality.
 *
 *  The APIs in `wix-users` can only be used once the page has loaded. Therefore,
 *  you must use them in code that is contained in or is called from the
 *  [onReady()]($w.html#onReady) event handler or any element event handler.
 * @snippet [User-loggedIn.es6=Get whether the user is logged in or not]
 * @snippet [Users-currentUser.es6=Get the current user's information]
 * @readonly
 */

/**
 * @member role
 * @label property
 * @syntax get role(): String
 * @summary Gets the user's role.
 * @description
 *  Gets one of the following roles:
 *  + `"Admin"` — The owner of the site.
 *  + `"Member"` — A user who is logged in.
 *  + `"Visitor"` — A user who is not logged in.
 *
 *
 * @note
 *  The APIs in `wix-users` are only partially functional when previewing your site.
 *  View a published version of your site to see their complete functionality.
 *
 *  The APIs in `wix-users` can only be used once the page has loaded. Therefore,
 *  you must use them in code that is contained in or is called from the
 *  [onReady()]($w.html#onReady) event handler or any element event handler.
 * @type {external:String}
 * @memberof wix-users.User
 * @snippet [User-role.es6=Get the user's role]
 * @snippet [Users-currentUser.es6=Get the current user's information]
 * @readonly
 */

/**
 * @function getEmail
 * @summary Gets the email of the current user.
 * @syntax
 * function getEmail(): Promise&lt;String&gt;
 * @description
 * The `getEmail()` function returns a Promise that resolves to the email address
 * of the user that is logged in or rejects if the current user is not logged in.
 * @note
 *  The APIs in `wix-users` are only partially functional when previewing your site.
 *  View a published version of your site to see their complete functionality.
 *
 *  The APIs in `wix-users` can only be used once the page has loaded. Therefore,
 *  you must use them in code that is contained in or is called from the
 *  [onReady()]($w.html#onReady) event handler or any element event handler.
 * @returns {Promise}
 * @fulfill {external:String} The email address of the user that is logged in.
 * @reject {external:String} Message containing error that the current user is not logged in.
 * @snippet [User-getEmail.es6=Get the user's email address]
 * @snippet [Users-currentUser.es6=Get the current user's information]
 * @memberof wix-users.User
 * @instance
 */

function getEmail() {
    return new Promise((resolve, reject) => {
        if (!this.loggedIn) {
            reject(userErrors.NO_LOGGED_IN);
            return;
        }

        postMessage.sendMessage(postMessage.Intent.WIX_CODE_SITE_API, postMessage.MessageTypes.SM_CURRENT_USER_EMAIL, null, (email, error) => {
            if (error) {
                reject(error);
            } else {
                resolve(email);
            }
        });
    });
}

const UserRoles = {
    VISITOR: 'Visitor',
    MEMBER: 'Member',
    ADMIN: 'Admin'
};

const LoginStatus = {
    APPLICANT: 'APPLICANT',
    ACTIVE: 'ACTIVE'
};


class User {
    constructor(memberData) {
        if (memberData.uid) {
            this.id = memberData.uid;
            this.loggedIn = true;
            this.role = memberData.permissions ? UserRoles.ADMIN : UserRoles.MEMBER;
        } else {
            const RGI = platformUtils.getRGI();
            this.id = get(RGI.getSessionInfo(), 'svSession');
            this.loggedIn = false;
            this.role = UserRoles.VISITOR;
        }
        if (memberData.instance){
            this.instance = memberData.instance;
        }
        if (memberData.instanceId){
            this.instanceId = memberData.instanceId;
        }
        this.getEmail = getEmail.bind(this);
    }
}

class Users {

    constructor(appDefId){
        this.appDefId = appDefId;
    }


    /**
     * @member currentUser
     * @label property
     * @syntax
     * get currentUser(): User
     * @summary Gets the current user viewing the site.
     * @description
     *  Gets a [`User`](wix-users.User.html) object containing information about
     *  the user currently viewing the site.
     * @note
     *  The APIs in `wix-users` are only partially functional when previewing your site.
     *  View a published version of your site to see their complete functionality.
     *
     *  The APIs in `wix-users` can only be used once the page has loaded. Therefore,
     *  you must use them in code that is contained in or is called from the
     *  [onReady()]($w.html#onReady) event handler or any element event handler.
     * @snippet [Users-currentUser.es6=Get the current user's information]
     * @type {wix-users.User}
     * @memberof wix-users
     * @readonly
     */
    get currentUser() {
        const RGI = platformUtils.getRGI();
        const siteMemberData = RGI.getSiteMemberData(this.appDefId);

        return new User(siteMemberData);
    }

    /**
     * @typedef {external:Object} wix-users~LoginOptions
     * @summary An object used by the `promptLogin()` function to determine how the login dialog box appears.
     * @property {external:String} [mode] What type of login experience to present: `"login"` or `"signup"`. Defaults to the option chosen in the Member Signup Settings panel in the Editor.
     * @property {external:String} [lang] The two letter language code of the language to show the login form in. Defaults to `"en"` if the property doesn't exist or the given language is not one of the languages found in the Permissions tab of the Page Settings panel in the Editor.
     * @snippet [Users-promptLogin_options.es6=Prompt the current user to login]
     * @see [promptLogin( )](#promptLogin)
     */


    /**
     * @function promptLogin
     * @syntax
     * function promptLogin([options: LoginOptions]): Promise&lt;User&gt;
     * @summary Prompts the current site visitor to log in as a site member.
     * @description
     *  The `promptLogin()` function returns a Promise that resolves to the newly
     *  logged in user when the login has completed.
     *
     *  The `promptLogin()` function cannot be called before the page is ready.
     * @note
     *  The APIs in `wix-users` are only partially functional when previewing your site.
     *  View a published version of your site to see their complete functionality.
     *
     *  The APIs in `wix-users` can only be used once the page has loaded. Therefore,
     *  you must use them in code that is contained in or is called from the
     *  [onReady()]($w.html#onReady) event handler or any element event handler.
     * @param {wix-users~LoginOptions} options The options that determine how the login dialog box appears.
     * @returns {Promise}
     * @fulfill {wix-users.User} Information about the newly logged in user.
     * @reject {external:String} Message that the dialog was canceled, or any other reason the user failed to log in.
     * @snippet [Users-promptLogin.es6=Prompt the current user to login]
     * @snippet [Users-promptLogin_options.es6=Prompt the current user to login with given options]
     * @see [promptForgotPassword( )](#promptForgotPassword)
     * @memberof wix-users
     */
    promptLogin (options) {
        return new Promise((resolve, reject) => {
            const requestParams = {callOnCancel: true};
            const language = get(options, 'lang');
            const mode = get(options, 'mode');

            if (!validate('Users', 'promptLogin', options)) {
                reject();
                return;
            }

            if (mode) {
                requestParams.mode = mode;
            }

            if (language) {
                requestParams.language = language;
            }

            postMessage.sendMessage(postMessage.Intent.WIX_CODE_SITE_API,
                postMessage.MessageTypes.SM_REQUEST_LOGIN,
                requestParams,
                (data, error) => {
                    if (error) {
                        reject(error);
                    } else if (data && data.wasCancelled) {
                        reject(userErrors.CLOSE_DIALOG);
                    } else if (!data || !isObject(data.data)) {
                        reject(userErrors.NO_LOGGED_IN);
                    } else if (data.data.status === LoginStatus.APPLICANT) {
                        reject(userErrors.AWAITING_APPROVAL);
                    } else {
                        resolve(new User({
                            uid: data.data.id,
                            permissions: data.data.owner
                        }));
                    }
                });
        });
    }


    /**
     * @function logout
     * @syntax
     * function logout(): void
     * @summary Logs the current user out of the site.
     * @description
     *  The `logout()` function logs the current user out of the site.
     * @note
     *  The APIs in `wix-users` are only partially functional when previewing your site.
     *  View a published version of your site to see their complete functionality.
     *
     *  The APIs in `wix-users` can only be used once the page has loaded. Therefore,
     *  you must use them in code that is contained in or is called from the
     *  [onReady()]($w.html#onReady) event handler or any element event handler.
     * @snippet [Users-logout.es6=Log out the current user]
     * @memberof wix-users
     */
    logout () {
        return new Promise((resolve, reject) => {
            postMessage.sendMessage(postMessage.Intent.WIX_CODE_SITE_API, postMessage.MessageTypes.SM_LOGOUT, null, (data, error) => {
                if (error) {
                    reject(error);
                } else {
                    resolve();
                }
            });
        });
    }

    /**
     * @function promptForgotPassword
     * @syntax
     * function promptForgotPassword([language: String]): Promise&lt;void&gt;
     * @summary Prompts the current site visitor with a password reset.
     * @description
     *  The `promptForgotPassword()` function returns a Promise that resolves when
     *  the user has sumbitted the forgot password form.
     *
     *  The `promptForgotPassword()` function cannot be called before the page is ready.
     * @note
     *  The APIs in `wix-users` are only partially functional when previewing your site.
     *  View a published version of your site to see their complete functionality.
     *
     *  The APIs in `wix-users` can only be used once the page has loaded. Therefore,
     *  you must use them in code that is contained in or is called from the
     *  [onReady()]($w.html#onReady) event handler or any element event handler.
     * @param {external:String} [language] The language of the reset password form. Defaults to `"English"` if not passed or the given language is not one of the languages found in the Permissions tab of the Page Settings panel in the Editor.
     * @returns {Promise}
     * @fulfill {void}
     * @reject {external:String} Message that the dialog was canceled, user is already logged in, or any other reason the password reset failed.
     * @snippet [Users-promptForgotPassword.es6=Prompt the user with a password reset]
     * @snippet [Users-promptForgotPassword_options.es6=Prompt the current user to login with given language]
     * @see [promptLogin( )](#promptLogin)
     * @memberof wix-users
     */

    promptForgotPassword (language) {
        return new Promise((resolve, reject) => {
            const requestParams = isUndefined(language) ? {} : {language};

            if (!validate('Users', 'promptForgotPassword', requestParams)) {
                reject();
                return;
            }

            postMessage.sendMessage(postMessage.Intent.WIX_CODE_SITE_API, postMessage.MessageTypes.SM_FORGOT_PASSWORD, requestParams, (data, error) => {
                if (error) {
                    reject(error);
                } else {
                    resolve();
                }
            });
        });
    }

    /**
     * @callback $w.LoginHandler
     * @summary Function that runs when a user has logged in.
     * @param {wix-users.User} user The user that has logged in.
     */

    /**
     * @function onLogin
     * @syntax
     * function onLogin(handler: LoginHandler): void
     * callback LoginHandler(): void
     * @summary Sets the function that runs when a user logs in.
     * @description
     *  Use the `onLogin()` function for code you want to run after a user successfully
     *  logs into your site.
     *
     *  Usually, you want to call the `onLogin()` function in the **Site** tab of the
     *  code panel so that the `onLogin()` event handler will be run no matter which
     *  page on your site a user uses to log in.
     * @note
     *  The APIs in `wix-users` are only partially functional when previewing your site.
     *  View a published version of your site to see their complete functionality.
     *
     *  The APIs in `wix-users` can only be used once the page has loaded. Therefore,
     *  you must use them in code that is contained in or is called from the
     *  [onReady()]($w.html#onReady) event handler or any element event handler.
     * @param {$w.LoginHandler} handler The name of the function or the function expression to run when a user has logged in.
     * @snippet [Users-onLogin.es6=Run code when a user logs in]
     * @memberof wix-users
     *  @eventhandler
     *  @eventtype
     */
    onLogin (callback) {
        if (!validate('Users', 'onLogin', {onLogin: callback})) {
            return;
        }
        const RGI = platformUtils.getRGI();
        RGI.addOnUserLoginCallback(() => {
            callback(this.currentUser);
        });
    }

  /**
   * @typedef {external:Object} wix-users~TransactionalEmailOptions
   * @summary An object used when sending a Transactional Email.
   * @property {external:Object} variables An object with `key:value` pairs where each
   *  `key` is a variable in the email template created in Transactional Emails and its
   *  corresponding `value` is the value to insert into the template in place of
   *  variable. The values must be strings.
   * @snippet [Users-emailUser_variables.es6=Send a transactional email to the currently logged-in member]
   * @see [emailUser( )](#emailUser)
   */

  /**
   * @function emailUser
   * @syntax
   * function emailUser(emailId: String, toUser: string, [options: TransactionalEmailOptions]): Promise&lt;void&gt;
   * @summary Sends a Transactional Email to the currently logged-in site member.
   * @description
   *  The `emailUser()` function sends a Transactional Email to the currently
   *  logged-in site member.
   *
   *  Before using the `emailUser()` function, you will need to set up at least one
   *  Transactional Email. To learn more about Transactional Emails,
   *  see [About Transactional Emails](https://support.wix.com/en/article/about-transactional-emails).
   *  To learn how to create a Transactional Email, see
   *  [Creating a Transactional Email](https://support.wix.com/en/article/creating-a-transactional-email).
   *
   *  Specify which email to send by passing the email's ID in the `emailId` parameter.
   *
   *  Specify which member the email is sent to by passing the member's user ID in the `toUser`
   *  parameter. You can only send the email to the currently logged-in
   *  member. You can get that member's ID using the [`id`](wix-users.User.html#id) property
   *  of the [`currentUser`](#currentUser).
   *
   *  If the specified Transactional Email contains [variables](https://support.wix.com/en/article/using-variables-to-add-personalized-text),
   *  you can pass values for those variables using the optional `options` parameter.
   *  You pass a [`TransactionalEmailOptions`](#TransactionalEmailOptions) object
   *  which contains the values to be inserted into your email in place of the variables
   *  defined in that email. The values passed must be stings. If the object you
   *  pass to the `options` parameter does not contain a `key:value` pair for a
   *  variable in your Transactional Email, the fallback value defined when creating
   *  your Transactional Email will be inserted in place of the variable.
   *
   *  Note that Transactional Emails generates a code snippet for each of your email
   *  templates. The generated code includes the email's ID and keys for all the
   *  email's variable names. You can copy and paste the snippet into your code.
   *  Then you will need to define values for the `toUser` property and for each
   *  variable key. To learn how to use the generated snippet in your code, see
   *  [How to Send a Transactional Email with Code](https://support.wix.com/en/article/how-to-send-a-transactional-email-with-code).
   * @snippet [Users-emailUser.es6=Send a Transactional Email to the currently logged-in member]
   * @snippet [Users-emailUser_variables.es6=Send a Transactional Email with variable values]
   * @param {external:String} emailId The Email ID of the Transactional Email to send.
   * @param {external:String} toUser The User ID of the currently signed-in member.
   * @param {wix-users~TransactionalEmailOptions} [options] Variable values to insert into the email.
   * @returns {Promise}
   * @fulfill {void} When the email is sent.
   * @reject {external:String} Error message.
   * @memberof wix-users
   */
    emailUser (emailId, toUser, options) {
        return new Promise((resolve, reject) => {
            if (!validate('CRM', 'emailUser', {emailId, userId: toUser, emailOptions: options})) {
                reject();
                return;
            }
            postMessage.sendMessage(postMessage.Intent.WIX_CODE_SITE_API,
                'sendUserEmail',
                {emailId, contactId: toUser, options},
                (data, error) => {
                    if (error) {
                        const { status, responseText } = error;
                        let errorMessage;
                        try {
                            errorMessage = JSON.parse(responseText).message;
                        } catch (e) { /*do nothing */ }
                        reject(`${errorMessage || 'unknown failure'} (${status || 0})`);
                    } else {
                        resolve();
                    }
                });
        });
    }
}

export default Users;



// WEBPACK FOOTER //
// ./js/modules/users.es6